import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useEffect } from 'react';

import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { useQueryLoader } from '@sorare/core/src/hooks/graphql/useQueryLoader';
import { useEvents } from '@sorare/core/src/lib/events/useEvents';
import { queryLoader } from '@sorare/core/src/lib/navigation/queryLoader';

import { UserFixtureResults } from 'components/profile/UserFixtureResults';
import { UserFixtureResultsHeader } from 'components/profile/UserFixtureResultsHeader';
import { useLayoutData } from 'pages/my-circles/$eventType/$fixture/layout';

import type {
  MyCircleFixtureSlugPageQuery,
  MyCircleFixtureSlugPageQueryVariables,
} from './__generated__/page.graphql';
import type { PageParams } from './__generated__/routeParams';

const MY_CIRCLE_FIXTURE_SLUG_PAGE_QUERY = gql`
  query MyCircleFixtureSlugPageQuery(
    $user: String!
    $slug: String
    $cursor: String
  ) {
    user(slug: $user) {
      slug
      ...UserFixtureResultsHeader_user
    }
    so5 {
      so5Fixture(slug: $slug) {
        slug
        userFixtureResults(userSlug: $user) {
          slug
          completedThresholdsStreakTasksCount
          totalRewardConfigs {
            id
            ...UserFixtureResultsHeader_anyRewardConfigInterface
          }
        }
        ...UserFixtureResults_so5Fixture
        ...UserFixtureResultsHeader_so5Fixture
      }
    }
  }
  ${UserFixtureResults.fragments.so5Fixture}
  ${UserFixtureResultsHeader.fragments.user}
  ${UserFixtureResultsHeader.fragments.anyRewardConfigInterface}
  ${UserFixtureResultsHeader.fragments.so5Fixture}
` as TypedDocumentNode<
  MyCircleFixtureSlugPageQuery,
  MyCircleFixtureSlugPageQueryVariables
>;

export const loader = queryLoader<PageParams>(
  MY_CIRCLE_FIXTURE_SLUG_PAGE_QUERY,
  ({ fixture, slug }) => ({
    slug: fixture,
    user: slug,
  })
);

const MyCircleFixtureSlugPage = () => {
  const track = useEvents();
  const { data, loading, loadMore } =
    useQueryLoader<MyCircleFixtureSlugPageQuery>();
  const { refetchList } = useLayoutData();

  const { user, so5 } = data || {};
  const { so5Fixture } = so5 || {};

  useEffect(() => {
    if (user?.slug) {
      track('View My Circles User Profile Screen', {});
    }
  }, [track, user?.slug]);

  if (!user || !so5Fixture) {
    return null;
  }

  return (
    <Vertical>
      <UserFixtureResultsHeader
        so5Fixture={so5Fixture}
        user={user}
        refresh={refetchList}
        totalRewardConfigs={
          so5Fixture?.userFixtureResults?.totalRewardConfigs || []
        }
        completedThresholdsStreakTasksCount={
          so5Fixture?.userFixtureResults?.completedThresholdsStreakTasksCount
        }
      />
      <UserFixtureResults
        so5Fixture={so5Fixture}
        loadMoreUserResults={loadMore}
        moreResultsLoading={loading}
      />
    </Vertical>
  );
};

export default MyCircleFixtureSlugPage;
